import React, { useState, useEffect } from 'react';
import { FaWhatsapp, FaGift, FaClock, FaStar, FaFire, FaHeart } from 'react-icons/fa';
import { promotions, generatePromoWhatsAppLink } from '../data/products';

const Promotions = () => {
    const [timeLeft, setTimeLeft] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    });

    // Contador regresivo (7 días desde ahora)
    useEffect(() => {
        const targetDate = new Date();
        targetDate.setDate(targetDate.getDate() + 7);

        const timer = setInterval(() => {
            const now = new Date().getTime();
            const distance = targetDate.getTime() - now;

            if (distance < 0) {
                clearInterval(timer);
                return;
            }

            setTimeLeft({
                days: Math.floor(distance / (1000 * 60 * 60 * 24)),
                hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
                seconds: Math.floor((distance % (1000 * 60)) / 1000),
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    return (
        <section
            id="promociones"
            className="py-20 md:py-28 relative overflow-hidden bg-gradient-to-b from-dulce-cream-50 via-amber-50/30 to-dulce-cream-50"
        >
            {/* Elementos decorativos de fondo */}
            <div className="absolute top-10 left-10 text-6xl opacity-10 animate-float">🎁</div>
            <div className="absolute top-40 right-20 text-5xl opacity-10 animate-float-slow">✨</div>
            <div className="absolute bottom-20 left-1/4 text-4xl opacity-10 animate-bounce-soft">💝</div>
            <div className="absolute bottom-40 right-1/3 text-5xl opacity-10 animate-float">🎉</div>

            {/* Círculos decorativos */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-amber-200/30 rounded-full translate-x-1/2 -translate-y-1/2 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-200/30 rounded-full -translate-x-1/2 translate-y-1/2 blur-3xl" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header de sección */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-5 py-2 rounded-full mb-4 shadow-lg animate-pulse-soft">
                        <FaFire className="animate-wiggle" />
                        <span className="text-sm font-bold tracking-widest uppercase">Ofertas Especiales</span>
                        <FaFire className="animate-wiggle" />
                    </div>

                    <h2 className="section-title text-gray-800">
                        Promociones{' '}
                        <span className="bg-gradient-to-r from-amber-600 via-orange-500 to-red-500 bg-clip-text text-transparent">
                            Irresistibles
                        </span>
                    </h2>
                    <p className="section-subtitle text-amber-700">
                        ¡Aprovecha nuestras ofertas por tiempo limitado! 🎉
                    </p>
                    <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-orange-400 mx-auto rounded-full" />
                </div>

                {/* Contador regresivo */}
                <div className="max-w-3xl mx-auto mb-12">
                    <div className="bg-gradient-to-r from-amber-900 via-amber-800 to-amber-900 rounded-2xl p-6 shadow-2xl">
                        <div className="flex items-center justify-center gap-2 text-amber-200 text-sm mb-3">
                            <FaClock className="animate-pulse" />
                            <span className="font-semibold tracking-widest uppercase">Oferta termina en:</span>
                        </div>
                        <div className="grid grid-cols-4 gap-2 md:gap-4">
                            {[
                                { label: 'Días', value: timeLeft.days },
                                { label: 'Horas', value: timeLeft.hours },
                                { label: 'Min', value: timeLeft.minutes },
                                { label: 'Seg', value: timeLeft.seconds },
                            ].map((item, i) => (
                                <div
                                    key={i}
                                    className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 text-center border border-white/10"
                                >
                                    <div className="text-2xl md:text-4xl font-bold text-white font-playfair">
                                        {String(item.value).padStart(2, '0')}
                                    </div>
                                    <div className="text-[10px] md:text-xs text-amber-200 uppercase tracking-wider mt-1">
                                        {item.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Promociones */}
                <div className="space-y-12">
                    {promotions.map((promo, index) => (
                        <PromotionCard key={promo.id} promo={promo} index={index} />
                    ))}
                </div>

                {/* CTA final */}
                <div className="mt-16 text-center">
                    <div className="inline-flex flex-col items-center gap-4 bg-white/60 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-amber-200">
                        <div className="flex items-center gap-2 text-amber-800">
                            <FaHeart className="animate-heart-beat text-red-500" />
                            <span className="font-semibold">¿Te gustó la promoción?</span>
                            <FaHeart className="animate-heart-beat text-red-500" />
                        </div>
                        <p className="text-gray-600 text-sm max-w-md">
                            No dejes pasar esta oportunidad. Escríbenos por WhatsApp y aprovecha nuestras ofertas
                            especiales.
                        </p>
                        <a
                            href={generatePromoWhatsAppLink('Promociones Dulce Mora')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-4 px-8 rounded-full shadow-2xl shadow-green-500/30 transform hover:scale-105 transition-all duration-300 text-lg"
                        >
                            <FaWhatsapp size={24} />
                            <span>¡Quiero mi promoción!</span>
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

// ==================== PROMOTION CARD ====================
const PromotionCard = ({ promo, index }) => {
    const whatsappLink = generatePromoWhatsAppLink(promo.title);

    return (
        <div
            className="relative bg-white rounded-3xl shadow-2xl overflow-hidden group hover:shadow-3xl transition-all duration-500 border-2 border-amber-200"
            style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.15}s both`,
            }}
        >
            {/* Ribbon superior */}
            <div className="absolute top-0 right-0 z-20">
                <div className="relative">
                    <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-2 font-bold text-xs tracking-widest uppercase shadow-lg transform rotate-0 md:rotate-0">
                        <span className="flex items-center gap-2">
                            <FaFire /> {promo.tag}
                        </span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Lado izquierdo - Imagen del producto */}
                <div className="relative overflow-hidden bg-gradient-to-br from-amber-100 to-orange-100 min-h-[400px] lg:min-h-[600px]">
                    <img
                        src={promo.image}
                        alt={promo.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

                    {/* Título flotante sobre imagen */}
                    <div className="absolute top-6 left-6 z-10">
                        <div className="bg-white/90 backdrop-blur-md rounded-2xl px-5 py-3 shadow-xl">
                            <p className="text-xs text-amber-700 tracking-widest uppercase font-semibold">
                                {promo.title}
                            </p>
                            <p className="font-great-vibes text-3xl md:text-4xl text-amber-900 leading-tight">
                                {promo.subtitle}
                            </p>
                            <p className="text-[10px] text-amber-800 mt-1 font-medium">
                                {promo.tagline.toUpperCase()}
                            </p>
                        </div>
                    </div>

                    {/* Badge de descuento */}
                    <div className="absolute bottom-6 right-6 z-10">
                        <div className="bg-gradient-to-br from-red-500 to-red-700 text-white rounded-full w-20 h-20 md:w-24 md:h-24 flex flex-col items-center justify-center shadow-2xl transform rotate-12 group-hover:rotate-0 transition-transform duration-500 border-4 border-white">
                            <span className="text-[10px] font-semibold">HASTA</span>
                            <span className="text-2xl md:text-3xl font-black leading-none">{promo.discount}</span>
                            <span className="text-[10px] font-semibold">OFF</span>
                        </div>
                    </div>

                    {/* Rating flotante */}
                    <div className="absolute bottom-6 left-6 z-10">
                        <div className="bg-white/95 backdrop-blur-md rounded-full px-3 py-1.5 flex items-center gap-1 shadow-lg">
                            {[...Array(5)].map((_, i) => (
                                <FaStar key={i} className="text-amber-400" size={10} />
                            ))}
                            <span className="text-xs text-gray-700 font-semibold ml-1">5.0</span>
                        </div>
                    </div>
                </div>

                {/* Lado derecho - Detalles y promoción */}
                <div className={`p-6 md:p-8 lg:p-10 bg-gradient-to-br ${promo.bgGradient} relative`}>
                    {/* Elementos decorativos */}
                    <div className="absolute top-4 right-4 text-4xl opacity-20 animate-float">✨</div>

                    {/* Badge PROMO */}
                    <div className="mb-6">
                        <div className="inline-flex items-center gap-2">
                            <div className="bg-gradient-to-r from-amber-900 to-amber-800 text-white px-5 py-2 rounded-xl shadow-lg transform -rotate-2">
                                <span className="text-2xl font-black tracking-wider">{promo.badge}</span>
                            </div>
                            <FaGift className="text-3xl text-amber-700 animate-bounce-soft" />
                        </div>
                    </div>

                    {/* Descripción principal */}
                    <h3 className={`font-playfair text-2xl md:text-3xl font-bold ${promo.accentColor} mb-2 leading-tight`}>
                        Compra tu porción de{' '}
                        <span className="text-red-700">{promo.title.toLowerCase()}</span>
                    </h3>
                    <p className="text-amber-800 text-sm mb-4 font-semibold">(2 discos)</p>

                    {/* Y llévate */}
                    <div className="my-6">
                        <div className="flex items-center gap-3 mb-2">
                            <span className="text-amber-500 text-2xl">✧</span>
                            <span className="font-great-vibes text-3xl md:text-4xl text-amber-800">
                                y llévate
                            </span>
                            <span className="text-amber-500 text-2xl">✧</span>
                        </div>

                        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl p-4 md:p-5 shadow-xl transform -rotate-1 hover:rotate-0 transition-transform duration-300">
                            <p className="text-2xl md:text-3xl font-black text-center tracking-wider">
                                1 PORCIÓN MÁS
                            </p>
                            <p className="text-3xl md:text-4xl font-black text-center text-amber-200 mt-1">
                                ¡GRATIS!
                            </p>
                            <p className="text-center text-xs text-amber-100 mt-1">(2 discos más)</p>
                        </div>
                    </div>

                    {/* Visual: Tú compras + Te regalamos */}
                    <div className="grid grid-cols-2 gap-3 my-6">
                        {/* Tú compras */}
                        <div className="bg-white rounded-2xl p-3 shadow-lg border-2 border-amber-200 relative overflow-hidden group/card hover:scale-105 transition-transform duration-300">
                            <div className="bg-gradient-to-r from-amber-800 to-amber-900 text-white text-center py-1.5 px-2 rounded-lg mb-2">
                                <span className="text-[10px] md:text-xs font-bold tracking-wider">
                                    {promo.promoDetails.buy.title}
                                </span>
                            </div>
                            <img
                                src={promo.productImage}
                                alt="Producto"
                                className="w-full h-24 md:h-32 object-cover rounded-lg mb-2"
                            />
                            <p className="text-center text-xs md:text-sm font-bold text-amber-900">
                                {promo.promoDetails.buy.description}
                            </p>
                            <p className="text-center text-[10px] text-amber-700">
                                {promo.promoDetails.buy.subtitle}
                            </p>
                        </div>

                        {/* Símbolo + */}
                        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 hidden">
                            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-xl border-2 border-amber-300">
                                <span className="text-2xl font-bold text-amber-800">+</span>
                            </div>
                        </div>

                        {/* Te regalamos */}
                        <div className="bg-white rounded-2xl p-3 shadow-lg border-2 border-red-200 relative overflow-hidden group/card hover:scale-105 transition-transform duration-300">
                            <div className="absolute top-2 right-2 z-10">
                                <FaGift className="text-red-500 animate-bounce-soft" size={16} />
                            </div>
                            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white text-center py-1.5 px-2 rounded-lg mb-2">
                                <span className="text-[10px] md:text-xs font-bold tracking-wider">
                                    {promo.promoDetails.gift.title}
                                </span>
                            </div>
                            <img
                                src={promo.productImage}
                                alt="Regalo"
                                className="w-full h-24 md:h-32 object-cover rounded-lg mb-2"
                            />
                            <p className="text-center text-xs md:text-sm font-bold text-red-700">
                                {promo.promoDetails.gift.description}
                            </p>
                            <p className="text-center text-[10px] text-red-600">
                                {promo.promoDetails.gift.subtitle}
                            </p>
                        </div>
                    </div>

                    {/* Precios */}
                    <div className="flex items-center justify-center gap-4 my-6 py-4 border-t-2 border-b-2 border-amber-200 border-dashed">
                        <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Antes</p>
                            <p className="text-xl md:text-2xl font-bold text-gray-400 line-through">
                                ${promo.originalPrice}
                            </p>
                        </div>
                        <div className="text-4xl text-amber-500">→</div>
                        <div className="text-center">
                            <p className="text-xs text-amber-700 mb-1 font-semibold">AHORA</p>
                            <p className="text-3xl md:text-4xl font-black text-red-600">
                                ${promo.price}
                            </p>
                        </div>
                    </div>

                    {/* Botón CTA */}
                    <a
                        href={whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-4 px-6 rounded-2xl shadow-xl transform hover:scale-[1.02] transition-all duration-300 text-lg group/btn"
                    >
                        <FaWhatsapp size={24} className="group-hover/btn:animate-wiggle" />
                        <span>¡Quiero esta promo!</span>
                    </a>

                    <p className="text-center text-xs text-amber-700 mt-3 font-medium">
                        🕐 Válido hasta el {promo.validUntil} · Pedidos con 24 hrs de anticipación
                    </p>
                </div>
            </div>

            {/* Footer de features */}
            <div className="bg-gradient-to-r from-amber-900 via-amber-800 to-amber-900 py-4 px-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-white text-center">
                    {promo.features.map((feature, i) => (
                        <div key={i} className="flex items-center justify-center gap-2">
                            <span className="text-xl md:text-2xl">{feature.icon}</span>
                            <div className="text-left">
                                <p className="text-xs md:text-sm font-bold leading-tight">{feature.title}</p>
                                <p className="text-[10px] text-amber-200 hidden md:block">{feature.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Promotions;